/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Warning } from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Feature } from '@turf/helpers';
import { MultiPolygon, Polygon } from 'geojson';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import React, { useContext, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  RootState,
  selectAntennaDropDownDisabled,
  selectBeamsOnMap,
  selectCurrentBeams,
  selectCurrentNextBeamsChecked,
  selectCurrentNextBeamsDisabled,
  selectFlightLegServiceAlerts,
  selectFlightPathBeamsChecked,
  selectFlightPathBeamsDisabled,
  selectFlightPathWaypointsChecked,
  selectFlightPathWaypointsDisabled,
  selectHcxHnaZonesChecked,
  selectKaHnaZonesChecked,
  selectKuHnaZonesChecked,
  selectGxHnaZonesChecked,
  selectKuHnaZonesDisabled,
  selectKaHnaZonesDisabled,
  selectHcxHnaZonesDisabled,
  selectGxHnaZonesDisabled,
  selectNextBeam,
  selectOpenedExpansionPanelId,
  selectSelectedAntennaTypeId,
  selectSelectedOverlaidFlightLegId,
  selectOverlaysCurrentContext,
  selectTerminals,
  selectHeatMapsEnabled,
} from '../../../store';
import * as GlobalActions from '../../../store/actions';
import * as BeamActions from '../../../store/beams/actions';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../utils/FeatureFlagUtils';
import DropDown, { DropDownItem } from '../../materialui_wrappers/DropDown';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import HeatMapsControls from './HeatMapsControls';
import NetworkToggle, { NetworkToggleItem } from './NetworkToggle';
import { BEAMS_NETWORK_GROUP_IDS, DISABLED_OPACITY, ENABLED_OPACITY } from './overlaysDefn';
import { BeamOnMap, OverlaysContextsEnum } from '../../../store/maps/reducer';
import RTEGroundStationControls from './RTEGroundStationControls';
import { getArcLightMapVersion } from '../../../utils/BeamUtils';

import {
  AlternativeContainer,
  DROPDOWN_WIDTH,
  DropDownWrapper,
  LabeledCheckbox,
  StyledButton,
  StyledSection,
} from './CommonOverlayComponents';

interface GroupedBeams {
  kaBeams: NetworkToggleItem[];
  hcxBeams: NetworkToggleItem[];
  kuBeams: NetworkToggleItem[];
  vs3Beams: NetworkToggleItem[];
  gxBeams: NetworkToggleItem[];
}

const sortBeamsByLabel = (a: NetworkToggleItem, b: NetworkToggleItem) =>
  a.label === b.label ? 0 : a.label > b.label ? 1 : -1;

/**
 * Helper to determine if a beam belongs to a specific network group
 */
const isBeamInNetworkGroup = (specialBeamsNetworkMapping: any, groupId: string, beamId: string): boolean => {
  return !isNil(specialBeamsNetworkMapping?.[groupId]) && specialBeamsNetworkMapping[groupId].includes(beamId);
};

/**
 * Helper to calculate HNA zone opacity
 */
const getHnaZoneOpacity = (
  isOtherZoneChecked: boolean,
  isDisabled: boolean,
  selectedAntennaTypeId: number | undefined,
): number => {
  return isOtherZoneChecked || isDisabled || selectedAntennaTypeId === undefined ? DISABLED_OPACITY : ENABLED_OPACITY;
};

/**
 * Helper to check if HNA zone should be disabled
 */
const isHnaZoneDisabled = (
  isDisabled: boolean,
  selectedAntennaTypeId: number | undefined,
  isOtherZoneChecked: boolean,
): boolean => {
  return isDisabled || selectedAntennaTypeId === undefined || isOtherZoneChecked;
};

/**
 * Reducer function to group beams by network type
 */
export const createGroupBeamsReducer = (specialBeamsNetworkMapping: any) => {
  return (acc: GroupedBeams, beam: NetworkToggleItem): GroupedBeams => {
    if (isNil(specialBeamsNetworkMapping)) {
      return acc;
    }
    if (isBeamInNetworkGroup(specialBeamsNetworkMapping, BEAMS_NETWORK_GROUP_IDS.Ka, beam.id)) {
      acc.kaBeams.push(beam);
    } else if (isBeamInNetworkGroup(specialBeamsNetworkMapping, BEAMS_NETWORK_GROUP_IDS.HCX, beam.id)) {
      acc.hcxBeams.push(beam);
    } else if (isBeamInNetworkGroup(specialBeamsNetworkMapping, BEAMS_NETWORK_GROUP_IDS.VS3, beam.id)) {
      acc.vs3Beams.push(beam);
    } else if (isBeamInNetworkGroup(specialBeamsNetworkMapping, BEAMS_NETWORK_GROUP_IDS.GX, beam.id)) {
      acc.gxBeams.push(beam);
    } else {
      acc.kuBeams.push(beam);
    }
    return acc;
  };
};

/**
 * Helper to create network toggle items from beam features
 */
export const createBeamToggleItems = (
  beams: any,
  selectedAntennaTypeId: number | undefined,
  beamsOnMap: BeamOnMap[],
): NetworkToggleItem[] => {
  if (!beams || !selectedAntennaTypeId) return [];

  const beamsArr: NetworkToggleItem[] = [];
  beams.features
    .filter((feature: Feature<MultiPolygon | Polygon>) => (feature.properties || {}).mapType !== 'HNA')
    .forEach((feature: Feature<MultiPolygon | Polygon>) => {
      if (feature.properties) {
        beamsArr.push({
          id: feature.properties.name,
          label: feature.properties.displayName,
          checked: !!beamsOnMap.find(
            (beam: BeamOnMap) =>
              beam.beamId === feature.properties?.name &&
              beam.networkType === feature.properties?.network &&
              beam.mapType === feature.properties?.mapType,
          ),
          networkType: feature.properties.network,
          mapType: feature.properties.mapType,
        });
      }
    });

  return beamsArr;
};

/**
 * Sub-component for rendering HNA Zones checkboxes
 */
const HnaZonesSection: React.FC<{
  appConfig: any;
  overlaysCurrentContext: OverlaysContextsEnum;
  selectedAntennaTypeId: number | undefined;
  kuHnaZonesChecked: boolean;
  kaHnaZonesChecked: boolean;
  hcxHnaZonesChecked: boolean;
  gxHnaZonesChecked: boolean;
  kuHnaZonesDisabled: boolean;
  kaHnaZonesDisabled: boolean;
  hcxHnaZonesDisabled: boolean;
  gxHnaZonesDisabled: boolean;
  onKuChange: (checked: boolean) => void;
  onKaChange: (checked: boolean) => void;
  onHcxChange: (checked: boolean) => void;
  onGxChange: (checked: boolean) => void;
}> = ({
  appConfig,
  overlaysCurrentContext,
  selectedAntennaTypeId,
  kuHnaZonesChecked,
  kaHnaZonesChecked,
  hcxHnaZonesChecked,
  gxHnaZonesChecked,
  kuHnaZonesDisabled,
  kaHnaZonesDisabled,
  hcxHnaZonesDisabled,
  gxHnaZonesDisabled,
  onKuChange,
  onKaChange,
  onHcxChange,
  onGxChange,
}) => {
  if (overlaysCurrentContext === OverlaysContextsEnum.TRANSPORT_MONITORING) return null;

  return (
    <>
      <div
        className="overlays__header"
        style={{ opacity: selectedAntennaTypeId === undefined ? DISABLED_OPACITY : ENABLED_OPACITY }}
      >
        RESTRICTED AREAS
      </div>

      <LabeledCheckbox
        classes={{ label: 'label' }}
        style={{
          opacity: getHnaZoneOpacity(
            kaHnaZonesChecked || hcxHnaZonesChecked || gxHnaZonesChecked,
            kuHnaZonesDisabled,
            selectedAntennaTypeId,
          ),
        }}
        control={
          <Checkbox
            checked={kuHnaZonesChecked}
            disabled={isHnaZoneDisabled(
              kuHnaZonesDisabled,
              selectedAntennaTypeId,
              kaHnaZonesChecked || hcxHnaZonesChecked || gxHnaZonesChecked,
            )}
            style={{ color: 'white' }}
            disableRipple={true}
            value="hna"
            onChange={(event, checked) => onKuChange(checked)}
          />
        }
        label="AL HNA Zones"
      />

      {isFeatureEnabled(appConfig, FEATURE_FLAGS.areKaHnaZonesEnabled) && (
        <LabeledCheckbox
          classes={{ label: 'label' }}
          style={{
            opacity: getHnaZoneOpacity(
              kuHnaZonesChecked || hcxHnaZonesChecked || gxHnaZonesChecked,
              kaHnaZonesDisabled,
              selectedAntennaTypeId,
            ),
          }}
          control={
            <Checkbox
              checked={kaHnaZonesChecked}
              disabled={isHnaZoneDisabled(
                kaHnaZonesDisabled,
                selectedAntennaTypeId,
                kuHnaZonesChecked || hcxHnaZonesChecked || gxHnaZonesChecked,
              )}
              style={{ color: 'white' }}
              disableRipple={true}
              value="hna"
              onChange={(event, checked) => onKaChange(checked)}
            />
          }
          label="VI HNA Zones"
        />
      )}

      {isFeatureEnabled(appConfig, FEATURE_FLAGS.areHcxHnaZonesEnabled) && (
        <LabeledCheckbox
          classes={{ label: 'label' }}
          style={{
            opacity: getHnaZoneOpacity(
              kuHnaZonesChecked || kaHnaZonesChecked || gxHnaZonesChecked,
              hcxHnaZonesDisabled,
              selectedAntennaTypeId,
            ),
          }}
          control={
            <Checkbox
              checked={hcxHnaZonesChecked}
              disabled={isHnaZoneDisabled(
                hcxHnaZonesDisabled,
                selectedAntennaTypeId,
                kuHnaZonesChecked || kaHnaZonesChecked || gxHnaZonesChecked,
              )}
              style={{ color: 'white' }}
              disableRipple={true}
              value="hna"
              onChange={(event, checked) => onHcxChange(checked)}
            />
          }
          label="HCX HNA Zones"
        />
      )}

      {isFeatureEnabled(appConfig, FEATURE_FLAGS.areGxHnaZonesEnabled) && (
        <LabeledCheckbox
          classes={{ label: 'label' }}
          style={{
            opacity: getHnaZoneOpacity(
              kuHnaZonesChecked || kaHnaZonesChecked || hcxHnaZonesChecked,
              gxHnaZonesDisabled,
              selectedAntennaTypeId,
            ),
          }}
          control={
            <Checkbox
              checked={gxHnaZonesChecked}
              disabled={isHnaZoneDisabled(
                gxHnaZonesDisabled,
                selectedAntennaTypeId,
                kuHnaZonesChecked || kaHnaZonesChecked || hcxHnaZonesChecked,
              )}
              style={{ color: 'white' }}
              disableRipple={true}
              value="hna"
              onChange={(event, checked) => onGxChange(checked)}
            />
          }
          label="GX HNA Zones"
        />
      )}
    </>
  );
};

/**
 * Sub-component for rendering Mission Overlay and Current Beam sections
 */
const MissionOverlayAndCurrentBeamSection: React.FC<{
  isFleetView: boolean;
  overlaysCurrentContext: OverlaysContextsEnum;
  isPerformanceOutlookView: boolean;
  flightPathBeamsDisabled: boolean;
  flightPathBeamsChecked: boolean;
  flightPathWaypointsDisabled: boolean;
  flightPathWaypointsChecked: boolean;
  currentNextBeamsDisabled: boolean;
  currentNextBeamsChecked: boolean;
  selectedOverlaidFlightLegId: string | null;
  selectedAntennaTypeId: number | undefined;
  onFlightPathBeamsChange: (checked: boolean) => void;
  onFlightPathWaypointsChange: (checked: boolean) => void;
  onCurrentBeamChange: (checked: boolean) => void;
}> = ({
  isFleetView,
  overlaysCurrentContext,
  isPerformanceOutlookView,
  flightPathBeamsDisabled,
  flightPathBeamsChecked,
  flightPathWaypointsDisabled,
  flightPathWaypointsChecked,
  currentNextBeamsDisabled,
  currentNextBeamsChecked,
  selectedOverlaidFlightLegId,
  selectedAntennaTypeId,
  onFlightPathBeamsChange,
  onFlightPathWaypointsChange,
  onCurrentBeamChange,
}) => {
  if (isFleetView || overlaysCurrentContext === OverlaysContextsEnum.TRANSPORT_MONITORING) return null;

  return (
    <>
      <div
        className="overlays__header"
        style={{ opacity: flightPathBeamsDisabled ? DISABLED_OPACITY : ENABLED_OPACITY }}
      >
        MISSION OVERLAY
      </div>

      <LabeledCheckbox
        classes={{ label: 'label' }}
        style={{
          opacity: flightPathBeamsDisabled || isNil(selectedOverlaidFlightLegId) ? DISABLED_OPACITY : ENABLED_OPACITY,
        }}
        control={
          <Checkbox
            id="flightPathBeamsCheckbox"
            checked={flightPathBeamsChecked}
            disabled={flightPathBeamsDisabled || isNil(selectedOverlaidFlightLegId)}
            style={{ color: 'white' }}
            disableRipple={true}
            value="flightPathBeams"
            onChange={(event, checked) => onFlightPathBeamsChange(checked)}
          />
        }
        label="Flight Path Beams"
      />

      <LabeledCheckbox
        classes={{ label: 'label' }}
        style={{
          opacity:
            flightPathWaypointsDisabled || isNil(selectedOverlaidFlightLegId) ? DISABLED_OPACITY : ENABLED_OPACITY,
        }}
        control={
          <Checkbox
            id="flightPathWaypointsCheckbox"
            checked={flightPathWaypointsChecked}
            disabled={flightPathWaypointsDisabled || isNil(selectedOverlaidFlightLegId)}
            style={{ color: 'white' }}
            disableRipple={true}
            value="flightPathWaypoints"
            onChange={(event, checked) => onFlightPathWaypointsChange(checked)}
          />
        }
        label="Flight Path Waypoints"
      />

      {!isPerformanceOutlookView && (
        <>
          <div
            className="overlays__header"
            style={{ opacity: currentNextBeamsDisabled ? DISABLED_OPACITY : ENABLED_OPACITY }}
          >
            CURRENT BEAM
          </div>

          <LabeledCheckbox
            classes={{ label: 'label' }}
            style={{
              opacity: currentNextBeamsDisabled || isNil(selectedAntennaTypeId) ? DISABLED_OPACITY : ENABLED_OPACITY,
            }}
            control={
              <Checkbox
                id="currentNextBeamCheckbox"
                checked={currentNextBeamsChecked}
                disabled={currentNextBeamsDisabled || isNil(selectedAntennaTypeId)}
                style={{ color: 'white' }}
                disableRipple={true}
                value="currentNextBeams"
                onChange={(event, checked) => onCurrentBeamChange(checked)}
              />
            }
            label="Current beam"
          />
        </>
      )}
    </>
  );
};

/**
 * Sub-component for rendering Network Toggle sections
 */
const NetworkTogglesSections: React.FC<{
  appConfig: any;
  groupedBeams: GroupedBeams;
  openedExpansionPanelId: string | null;
  onExpansionClick: (id: string) => void;
  onCheckboxChange: (item: NetworkToggleItem, checked: boolean) => void;
}> = ({ appConfig, groupedBeams, openedExpansionPanelId, onExpansionClick, onCheckboxChange }) => {
  return (
    <>
      {groupedBeams.kaBeams.length !== 0 && (
        <NetworkToggle
          id={BEAMS_NETWORK_GROUP_IDS.Ka}
          data-test="network-overlays--ka-dropdown"
          title="Viasat Internet"
          disabled={false}
          expanded={openedExpansionPanelId === BEAMS_NETWORK_GROUP_IDS.Ka}
          handleExpansionButtonClick={() => onExpansionClick(BEAMS_NETWORK_GROUP_IDS.Ka)}
          handleCheckboxChange={onCheckboxChange}
          items={groupedBeams.kaBeams}
        />
      )}

      {groupedBeams.kuBeams.length !== 0 && (
        <NetworkToggle
          id={BEAMS_NETWORK_GROUP_IDS.Ku}
          data-test="network-overlays--ku-dropdown"
          title="ArcLight"
          disabled={false}
          expanded={openedExpansionPanelId === BEAMS_NETWORK_GROUP_IDS.Ku}
          handleExpansionButtonClick={() => onExpansionClick(BEAMS_NETWORK_GROUP_IDS.Ku)}
          handleCheckboxChange={onCheckboxChange}
          items={groupedBeams.kuBeams}
        />
      )}

      {isFeatureEnabled(appConfig, FEATURE_FLAGS.isHCXEnabled) && groupedBeams.hcxBeams.length !== 0 && (
        <NetworkToggle
          id={BEAMS_NETWORK_GROUP_IDS.HCX}
          data-test="network-overlays--hcx-dropdown"
          title="HCX"
          disabled={false}
          expanded={openedExpansionPanelId === BEAMS_NETWORK_GROUP_IDS.HCX}
          handleExpansionButtonClick={() => onExpansionClick(BEAMS_NETWORK_GROUP_IDS.HCX)}
          handleCheckboxChange={onCheckboxChange}
          items={groupedBeams.hcxBeams}
        />
      )}

      {isFeatureEnabled(appConfig, FEATURE_FLAGS.isVs3Enabled) && groupedBeams.vs3Beams.length !== 0 && (
        <NetworkToggle
          id={BEAMS_NETWORK_GROUP_IDS.VS3}
          data-test="network-overlays--vs3-dropdown"
          title="VS3"
          disabled={false}
          expanded={openedExpansionPanelId === BEAMS_NETWORK_GROUP_IDS.VS3}
          handleExpansionButtonClick={() => onExpansionClick(BEAMS_NETWORK_GROUP_IDS.VS3)}
          handleCheckboxChange={onCheckboxChange}
          items={groupedBeams.vs3Beams}
        />
      )}

      {isFeatureEnabled(appConfig, FEATURE_FLAGS.isGxEnabled) && groupedBeams.gxBeams.length !== 0 && (
        <NetworkToggle
          id={BEAMS_NETWORK_GROUP_IDS.GX}
          data-test="network-overlays--gx-dropdown"
          title="GX"
          disabled={false}
          expanded={openedExpansionPanelId === BEAMS_NETWORK_GROUP_IDS.GX}
          handleExpansionButtonClick={() => onExpansionClick(BEAMS_NETWORK_GROUP_IDS.GX)}
          handleCheckboxChange={onCheckboxChange}
          items={groupedBeams.gxBeams}
        />
      )}
    </>
  );
};

/**
 * Helper to process flight path beams based on service alerts
 */
export const processFlightPathBeams = (
  checked: boolean,
  overlaidFlightLegId: string | number | null | undefined,
  flightLegServiceAlerts: any,
  dispatch: any,
  addBeamToMap: any,
  removeBeamFromMap: any,
) => {
  const addOrRemoveBeamOnMap = checked ? addBeamToMap : removeBeamFromMap;

  if (
    overlaidFlightLegId &&
    flightLegServiceAlerts &&
    flightLegServiceAlerts[overlaidFlightLegId] &&
    size(flightLegServiceAlerts[overlaidFlightLegId].beams)
  ) {
    flightLegServiceAlerts[overlaidFlightLegId].beams.features.forEach((feature: Feature) => {
      dispatch(
        addOrRemoveBeamOnMap(feature.properties?.name, feature.properties?.network, feature.properties?.mapType),
      );
    });
  }
};

/**
 * Helper to process current and next beams
 */
export const processCurrentNextBeams = (
  checked: boolean,
  currentBeams: BeamOnMap[] | null | undefined,
  nextBeam: BeamOnMap | null | undefined,
  dispatch: any,
  addBeamToMap: any,
  removeBeamFromMap: any,
) => {
  const addOrRemoveBeamOnMap = checked ? addBeamToMap : removeBeamFromMap;

  if (!isNil(currentBeams) && size(currentBeams)) {
    currentBeams.forEach((beam: BeamOnMap) => {
      dispatch(addOrRemoveBeamOnMap(beam.beamId, beam.networkType, beam.mapType));
    });
  }

  if (!isNil(nextBeam)) {
    dispatch(addOrRemoveBeamOnMap(nextBeam.beamId, nextBeam.networkType, nextBeam.mapType));
  }
};

const NetworkOverlays = (props: any) => {
  const {
    requestBeamsByAntennaTypeId,
    selectedOverlaidFlightLegId,
    routerPathname,
    beams,
    isRequestingBeams,
    isBeamsRequestFinished,
    requestBeamsError,
    appConfig,
    isHeatMapsEnabled,
    specialBeamsNetworkMapping,
    isPerformanceOutlookView,
  } = props;

  const dispatch = useDispatch();
  const mapActionsContext = useContext(MapActionsContext);
  const {
    setOpenedExpansionPanelId,
    removeBeamFromMap,
    addBeamToMap,
    setCurrentNextBeamsChecked,
    setSelectedAntennaTypeId,
    clearBeamsOnMap,
    setKuHnaZonesChecked,
    setKaHnaZonesChecked,
    setHcxHnaZonesChecked,
    setGxHnaZonesChecked,
    setFlightPathBeamsChecked,
    setFlightPathWaypointsChecked,
    disableHeatMaps,
  } = mapActionsContext.mapActions;

  const selectedAntennaTypeId = useSelectorWithMapId(selectSelectedAntennaTypeId);
  const antennaDropDownDisabled = useSelectorWithMapId(selectAntennaDropDownDisabled);
  const openedExpansionPanelId = useSelectorWithMapId(selectOpenedExpansionPanelId);
  const beamsOnMap = useSelectorWithMapId(selectBeamsOnMap);
  const kuHnaZonesChecked = useSelectorWithMapId(selectKuHnaZonesChecked);
  const kaHnaZonesChecked = useSelectorWithMapId(selectKaHnaZonesChecked);
  const hcxHnaZonesChecked = useSelectorWithMapId(selectHcxHnaZonesChecked);
  const gxHnaZonesChecked = useSelectorWithMapId(selectGxHnaZonesChecked);
  const kuHnaZonesDisabled = useSelectorWithMapId(selectKuHnaZonesDisabled);
  const kaHnaZonesDisabled = useSelectorWithMapId(selectKaHnaZonesDisabled);
  const hcxHnaZonesDisabled = useSelectorWithMapId(selectHcxHnaZonesDisabled);
  const gxHnaZonesDisabled = useSelectorWithMapId(selectGxHnaZonesDisabled);
  const flightPathBeamsDisabled = useSelectorWithMapId(selectFlightPathBeamsDisabled);
  const flightPathBeamsChecked = useSelectorWithMapId(selectFlightPathBeamsChecked);
  const flightPathWaypointsDisabled = useSelectorWithMapId(selectFlightPathWaypointsDisabled);
  const flightPathWaypointsChecked = useSelectorWithMapId(selectFlightPathWaypointsChecked);
  const currentNextBeamsDisabled = useSelectorWithMapId(selectCurrentNextBeamsDisabled);
  const currentNextBeamsChecked = useSelectorWithMapId(selectCurrentNextBeamsChecked);
  const currentBeams = useSelectorWithMapId(selectCurrentBeams);
  const nextBeam = useSelectorWithMapId(selectNextBeam);
  const overlaidFlightLegId = useSelectorWithMapId(selectSelectedOverlaidFlightLegId);
  const heatMapsEnabled = useSelectorWithMapId(selectHeatMapsEnabled);

  const flightLegServiceAlerts = useSelector(selectFlightLegServiceAlerts);

  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext);

  const terminals = useSelector(selectTerminals);

  const sectionHeight = overlaysCurrentContext === OverlaysContextsEnum.TRANSPORT_MONITORING ? 56 : 220;

  const isFleetView = useMemo(() => routerPathname === '/fleet', [routerPathname]);

  const groupedBeams = useMemo(() => {
    const beamsArr = createBeamToggleItems(beams, selectedAntennaTypeId, beamsOnMap);
    const emptyGroups = { kaBeams: [], hcxBeams: [], kuBeams: [], vs3Beams: [], gxBeams: [] };

    if (!beamsArr.length) return emptyGroups;

    const reducer = createGroupBeamsReducer(specialBeamsNetworkMapping);
    return beamsArr.sort(sortBeamsByLabel).reduce(reducer, emptyGroups);
  }, [beams, beamsOnMap, specialBeamsNetworkMapping, selectedAntennaTypeId]);

  const opacityBasedOnAntennaSelection = useMemo(() => {
    return selectedAntennaTypeId === undefined ? DISABLED_OPACITY : ENABLED_OPACITY;
  }, [selectedAntennaTypeId]);

  const handleAntennaTypeSelection = (selectedAntennaType: DropDownItem) => {
    const { antennas } = props;

    if (selectedAntennaTypeId !== (selectedAntennaType.key as number)) {
      dispatch(clearBeamsOnMap());
      dispatch(setSelectedAntennaTypeId(selectedAntennaType.key as number));
      dispatch(setOpenedExpansionPanelId(null));

      // get new heat maps if enabled
      if (antennas && selectedAntennaType && antennas[selectedAntennaType.key as number] && heatMapsEnabled) {
        // disable heatmaps if enabled and the antenna changes
        dispatch(disableHeatMaps());
      }
    }
  };

  const getAntennasForDropDown = () => {
    const { antennas } = props;

    const visibleAntennas = new Set();
    Object.values(terminals)
      .filter(t => t.antennaTypeId != null)
      .forEach(t => visibleAntennas.add(t.antennaTypeId.toString()));
    if (antennas !== undefined) {
      return Object.keys(antennas)
        .filter(antennaKey => visibleAntennas.has(antennaKey))
        .map(antennaKey => {
          return { key: antennas[antennaKey].id, label: antennas[antennaKey].displayName };
        })
        .sort((a, b) => (a.label === b.label ? 0 : a.label > b.label ? 1 : -1));
    }

    return [];
  };

  const handleNetworkToggleUpdate = (networkToggleItem: NetworkToggleItem, checked: boolean) => {
    // Update the changed NetworkToggleItem
    networkToggleItem.checked = checked;

    const areCurrentBeamsOnMap = (newBeamsOnMap: BeamOnMap[]) =>
      currentBeams &&
      size(currentBeams) &&
      currentBeams.every((currentBeam: BeamOnMap) =>
        newBeamsOnMap.find(
          (newBeamOnMap: BeamOnMap) =>
            newBeamOnMap.beamId === currentBeam.beamId &&
            newBeamOnMap.networkType === currentBeam.networkType &&
            newBeamOnMap.mapType === currentBeam.mapType,
        ),
      );

    if (checked) {
      dispatch(addBeamToMap(networkToggleItem.id, networkToggleItem.networkType, networkToggleItem.mapType));

      const beamsOnMapWithAddedBeam = !isNil(beamsOnMap)
        ? beamsOnMap.concat([
            {
              beamId: networkToggleItem.id,
              networkType: networkToggleItem.networkType,
              mapType: networkToggleItem.mapType,
            },
          ])
        : [];

      if (areCurrentBeamsOnMap(beamsOnMapWithAddedBeam)) {
        dispatch(setCurrentNextBeamsChecked(true));
      }
    } else {
      dispatch(removeBeamFromMap(networkToggleItem.id, networkToggleItem.networkType, networkToggleItem.mapType));

      const beamsOnMapWithRemovedBeam = !isNil(beamsOnMap)
        ? beamsOnMap.filter(
            (beamOnMap: BeamOnMap) =>
              beamOnMap.beamId !== networkToggleItem.id &&
              beamOnMap.networkType !== networkToggleItem.networkType &&
              beamOnMap.mapType !== networkToggleItem.mapType,
          )
        : [];

      if (!areCurrentBeamsOnMap(beamsOnMapWithRemovedBeam)) {
        dispatch(setCurrentNextBeamsChecked(false));
      }
    }
  };

  const handleKuHnaZonesCheckedChange = (checked: boolean) => {
    dispatch(setKuHnaZonesChecked(checked));
  };
  const handleKaHnaZonesCheckedChange = (checked: boolean) => {
    dispatch(setKaHnaZonesChecked(checked));
  };
  const handleHcxHnaZonesCheckedChange = (checked: boolean) => {
    dispatch(setHcxHnaZonesChecked(checked));
  };
  const handleGxHnaZonesCheckedChange = (checked: boolean) => {
    dispatch(setGxHnaZonesChecked(checked));
  };
  const handleFlightPathBeamsCheckedChange = (checked: boolean) => {
    dispatch(setFlightPathBeamsChecked(checked));
    processFlightPathBeams(
      checked,
      overlaidFlightLegId,
      flightLegServiceAlerts,
      dispatch,
      addBeamToMap,
      removeBeamFromMap,
    );
  };
  const handleFlightPathWaypointsCheckedChange = (checked: boolean) => {
    dispatch(setFlightPathWaypointsChecked(checked));
  };
  const handleCurrentBeamNextBeamChecked = (checked: boolean) => {
    dispatch(setCurrentNextBeamsChecked(checked));
    processCurrentNextBeams(checked, currentBeams, nextBeam, dispatch, addBeamToMap, removeBeamFromMap);
  };
  return (
    <div>
      <DropDownWrapper>
        <DropDown
          initialKey={selectedAntennaTypeId}
          data-test="network-overlays--dropdown"
          placeholder="Select Antenna Type *"
          width={DROPDOWN_WIDTH}
          items={getAntennasForDropDown()}
          onItemSelected={handleAntennaTypeSelection}
          disabled={antennaDropDownDisabled || isRequestingBeams || Object.keys(terminals).length < 1}
        />
      </DropDownWrapper>

      {isRequestingBeams ? (
        <AlternativeContainer height={sectionHeight}>
          <CircularProgress className="progress" />
        </AlternativeContainer>
      ) : isBeamsRequestFinished && requestBeamsError ? (
        <AlternativeContainer height={sectionHeight}>
          <Warning />
          <div>Error Retrieving Beams for the Antenna Type</div>
          <StyledButton
            color={'primary'}
            data-test="network-overlays--retry"
            onClick={() => {
              if (selectedAntennaTypeId) {
                requestBeamsByAntennaTypeId(selectedAntennaTypeId);
              }
            }}
          >
            RETRY
          </StyledButton>
        </AlternativeContainer>
      ) : (
        <StyledSection height={sectionHeight}>
          {isHeatMapsEnabled && overlaysCurrentContext !== OverlaysContextsEnum.TRANSPORT_MONITORING ? (
            <>
              <div className="overlays__header" style={{ opacity: opacityBasedOnAntennaSelection }}>
                DATA RATE CONTOURS
              </div>

              <HeatMapsControls />
            </>
          ) : (
            <></>
          )}
          <div className="overlays__header" style={{ opacity: opacityBasedOnAntennaSelection }}>
            NETWORKS BEAMS & HUBS
          </div>

          <NetworkTogglesSections
            appConfig={appConfig}
            groupedBeams={groupedBeams}
            openedExpansionPanelId={openedExpansionPanelId}
            onExpansionClick={id => dispatch(setOpenedExpansionPanelId(id))}
            onCheckboxChange={handleNetworkToggleUpdate}
          />

          {overlaysCurrentContext === OverlaysContextsEnum.TRANSPORT_MONITORING && (
            <>
              <div className="overlays__header" style={{ opacity: opacityBasedOnAntennaSelection }}>
                GROUND STATIONS
              </div>

              <RTEGroundStationControls />
            </>
          )}
          <HnaZonesSection
            appConfig={appConfig}
            overlaysCurrentContext={overlaysCurrentContext}
            selectedAntennaTypeId={selectedAntennaTypeId}
            kuHnaZonesChecked={kuHnaZonesChecked}
            kaHnaZonesChecked={kaHnaZonesChecked}
            hcxHnaZonesChecked={hcxHnaZonesChecked}
            gxHnaZonesChecked={gxHnaZonesChecked}
            kuHnaZonesDisabled={kuHnaZonesDisabled}
            kaHnaZonesDisabled={kaHnaZonesDisabled}
            hcxHnaZonesDisabled={hcxHnaZonesDisabled}
            gxHnaZonesDisabled={gxHnaZonesDisabled}
            onKuChange={handleKuHnaZonesCheckedChange}
            onKaChange={handleKaHnaZonesCheckedChange}
            onHcxChange={handleHcxHnaZonesCheckedChange}
            onGxChange={handleGxHnaZonesCheckedChange}
          />

          <MissionOverlayAndCurrentBeamSection
            isFleetView={isFleetView}
            overlaysCurrentContext={overlaysCurrentContext}
            isPerformanceOutlookView={isPerformanceOutlookView}
            flightPathBeamsDisabled={flightPathBeamsDisabled}
            flightPathBeamsChecked={flightPathBeamsChecked}
            flightPathWaypointsDisabled={flightPathWaypointsDisabled}
            flightPathWaypointsChecked={flightPathWaypointsChecked}
            currentNextBeamsDisabled={currentNextBeamsDisabled}
            currentNextBeamsChecked={currentNextBeamsChecked}
            selectedOverlaidFlightLegId={selectedOverlaidFlightLegId}
            selectedAntennaTypeId={selectedAntennaTypeId}
            onFlightPathBeamsChange={handleFlightPathBeamsCheckedChange}
            onFlightPathWaypointsChange={handleFlightPathWaypointsCheckedChange}
            onCurrentBeamChange={handleCurrentBeamNextBeamChecked}
          />
        </StyledSection>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  specialBeamsNetworkMapping: state.config.appConfig.specialBeamsNetworkMapping,

  appConfig: state.config.appConfig,

  antennas: state.antennas.antennas,

  beams: state.beams.beams,
  isRequestingBeams: state.beams.isRequestingBeams,
  isBeamsRequestFinished: state.beams.isBeamsRequestFinished,
  requestBeamsError: state.beams.requestBeamsError,
  routerPathname: state.router.location.pathname,
  isHeatMapsEnabled: state.config.appConfig.isHeatMapsEnabled,
});

const mapDispatchToProps = {
  switchOverlaysContext: GlobalActions.switchOverlaysContext,
  requestBeamsByAntennaTypeId: (antennaTypeId: number) =>
    BeamActions.requestBeams(getArcLightMapVersion(antennaTypeId, undefined)),
};

export default connect(mapStateToProps, mapDispatchToProps)(NetworkOverlays);
